import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  CalendarClock, Link2, Unlink, PlugZap, CheckCircle2, XCircle, AlertTriangle, Pencil,
} from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import Button from '../../components/ui/Button';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import LoadErrorBanner from '../../components/ui/LoadErrorBanner';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import { useMicrosoftCalendarApi, msCalendarErrorKey } from './config/microsoftCalendar';

// ─────────────────────────────────────────────────────────────────────────
// Microsoft 365 takvim senkronu — İşe Alım Ayarları > Takvim sekmesi.
//
// Kart yerleşimi ve üç-sonuçlu test kutusu SlackIntegrationCard'ın dilini
// izler (aynı ürün, aynı "entegrasyon kartı" kavramı). İKİ FARK:
//   1. OAuth yönlendirmesi YOK — uygulama izni (client credentials) modelinde
//      BT üç değeri ELLE girer. Bu bir FORMDUR, dolayısıyla GenericFormDrawer
//      kullanılır (repo kuralı: form = çekmece, modal ASLA).
//   2. "Bağlı değil" ile "yüklenemedi" AYRI görünür (LoadErrorBanner) —
//      api.js okumalarda reddetmediği için ikisi kolayca karışır.
//
// İÇ İÇE SCROLL YOK: kart normal sayfa akışında durur, kendi scroll kabı
// YOKTUR.
// ─────────────────────────────────────────────────────────────────────────
export default function CalendarIntegrationTab() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const canManage = canWrite('AT');

  const {
    status, fetching, loadFailed, connect, disconnect, testConnection,
  } = useMicrosoftCalendarApi();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [disconnectConfirm, setDisconnectConfirm] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const connected = !!status?.connected;

  const handleSave = useCallback(async (values) => {
    const ok = await connect({
      azureTenantId: (values.azureTenantId || '').trim(),
      clientId: (values.clientId || '').trim(),
      clientSecret: (values.clientSecret || '').trim(),
    });
    if (ok) {
      setDrawerOpen(false);
      setTestResult(null);
    }
    return ok;
  }, [connect]);

  const handleDisconnect = useCallback(async () => {
    setDisconnecting(true);
    await disconnect();
    setDisconnecting(false);
    setDisconnectConfirm(false);
    setTestResult(null);
  }, [disconnect]);

  const handleTest = useCallback(async () => {
    setTesting(true);
    setTestResult(null);
    const result = await testConnection();
    setTesting(false);
    setTestResult(result);
  }, [testConnection]);

  // Gizli anahtar ASLA geri dönmez — çekmece her açılışta BOŞ gelir ve
  // ipucu "değiştirmek için yeniden girin" der. Azure kimlikleri gizli
  // DEĞİLDİR, mevcut değerleriyle ön-doldurulur (BT kurulumu doğrulayabilsin).
  // `notice` alanı GenericFormDrawer'ın açıklama tipidir (değer taşımaz,
  // zorunluluk denetimine girmez) — BT'nin üç değeri Azure portalında NEREDEN
  // alacağı burada yazar, yoksa ekran "üç kutu doldur" demekten ibaret kalır.
  const drawerSchema = [
    {
      name: '__msCalendarNotice',
      type: 'notice',
      content: t('integrations.msCalendar.drawerNotice'),
    },
    {
      name: 'azureTenantId',
      label: t('integrations.msCalendar.azureTenantIdLabel'),
      type: 'text',
      required: true,
    },
    {
      name: 'clientId',
      label: t('integrations.msCalendar.clientIdLabel'),
      type: 'text',
      required: true,
    },
    {
      name: 'clientSecret',
      label: t('integrations.msCalendar.clientSecretLabel'),
      // password: değer ekranda AÇIK GÖRÜNMEZ. Kaydedildikten sonra da geri
      // OKUNMAZ — çekmece her açılışta bu alanı BOŞ getirir (gizli anahtar
      // hiçbir yanıtta dönmediği için doldurulacak bir değer yoktur).
      type: 'password',
      required: true,
    },
    {
      name: '__msCalendarSecretNotice',
      type: 'notice',
      tone: 'warning',
      content: t('integrations.msCalendar.secretRewriteNotice'),
    },
  ];

  return (
    <Wrapper>
      {/* "YÜKLENEMEDİ" AYRI BİR DURUM: sunucuya ulaşılamadığında "bağlı değil"
          göstermek İK'yı var olan bir bağlantıyı yeniden kurmaya iterdi. */}
      {loadFailed && <LoadErrorBanner message={t('integrations.msCalendar.loadFailed')} />}

      <Card>
        <CardHeader>
          <IconWrap><CalendarClock size={20} /></IconWrap>
          <div>
            <CardTitle>{t('integrations.msCalendar.cardTitle')}</CardTitle>
            <CardSubtitle>{t('integrations.msCalendar.cardSubtitle')}</CardSubtitle>
          </div>
        </CardHeader>

        {fetching ? (
          <Description>{t('common.loading')}</Description>
        ) : !connected ? (
          <>
            <Description>{t('integrations.msCalendar.description')}</Description>
            <Description>{t('integrations.msCalendar.setupHint')}</Description>
            {canManage && (
              <Actions>
                <Button onClick={() => setDrawerOpen(true)} disabled={loadFailed}>
                  <Link2 size={14} />
                  {t('integrations.msCalendar.connectButton')}
                </Button>
              </Actions>
            )}
          </>
        ) : (
          <>
            <StatusRow>
              <Badge data-role="ms-calendar-status-badge" $tone={status.isEnabled !== false ? 'ok' : 'off'}>
                {t(status.isEnabled !== false
                  ? 'integrations.msCalendar.statusConnected'
                  : 'integrations.msCalendar.statusDisabled')}
              </Badge>
            </StatusRow>

            <FieldList>
              <FieldRow>
                <FieldLabel>{t('integrations.msCalendar.azureTenantIdLabel')}</FieldLabel>
                <FieldValue data-role="ms-calendar-azure-tenant">{status.azureTenantId || '—'}</FieldValue>
              </FieldRow>
              <FieldRow>
                <FieldLabel>{t('integrations.msCalendar.clientIdLabel')}</FieldLabel>
                <FieldValue data-role="ms-calendar-client-id">{status.clientId || '—'}</FieldValue>
              </FieldRow>
              {/* Gizli anahtar SATIRI YOK — kısmi gösterim bile yapılmaz. */}
              <FieldRow>
                <FieldLabel>{t('integrations.msCalendar.clientSecretLabel')}</FieldLabel>
                <FieldValue data-role="ms-calendar-secret-state">{t('integrations.msCalendar.secretStored')}</FieldValue>
              </FieldRow>
            </FieldList>

            {/* KURULUM HATASI ayrı bir uyarıdır: "auth" sınıfı BT'nin
                düzeltmesi gereken bir şeydir, geçici bir kesinti değil. */}
            {status.lastErrorClass && status.failedCount > 0 && (
              <ResultBox
                data-role="ms-calendar-failure-hint"
                $tone={status.lastErrorClass === 'auth' ? 'error' : 'warning'}
              >
                <AlertTriangle size={16} />
                <span>
                  {t(msCalendarErrorKey(status.lastErrorClass))}
                  {' '}
                  {/* Değişken adı `failed` — `count` DEĞİL: i18next'te `count`
                      ÇOĞUL çözümünü tetikler (`..._one` / `..._other` arar) ve
                      bu anahtar ailesinde o varyantlar YOK. Ayrıca locale
                      dizesi `{{failed}}` yer tutucusunu taşır; `count`
                      geçildiğinde ekrana ham `{{failed}}` basılırdı. */}
                  {t('integrations.msCalendar.failedCount', { failed: status.failedCount })}
                </span>
              </ResultBox>
            )}

            {canManage && (
              <Actions>
                <Button variant="outline" onClick={handleTest} disabled={testing} loading={testing}>
                  <PlugZap size={14} />
                  {testing ? t('integrations.msCalendar.testing') : t('integrations.msCalendar.testButton')}
                </Button>
                <Button variant="outline" onClick={() => setDrawerOpen(true)}>
                  <Pencil size={14} />
                  {t('integrations.msCalendar.editButton')}
                </Button>
                <Button variant="outline" onClick={() => setDisconnectConfirm(true)} disabled={disconnecting}>
                  <Unlink size={14} />
                  {t('integrations.msCalendar.disconnectButton')}
                </Button>
              </Actions>
            )}

            {testResult && (
              testResult.ok ? (
                testResult.data?.status ? (
                  <ResultBox data-role="ms-calendar-test-success" $tone="success">
                    <CheckCircle2 size={16} />
                    <span>{t('integrations.msCalendar.testSuccess')}</span>
                  </ResultBox>
                ) : (
                  // ÜÇ HATA SINIFI, ÜÇ AYRI METİN. "auth" kurulum hatasıdır
                  // (kırmızı, BT'ye iş düşer); "transient" geçicidir (sarı).
                  <ResultBox
                    data-role="ms-calendar-test-failed"
                    $tone={testResult.data?.errorClass === 'transient' ? 'warning' : 'error'}
                  >
                    <XCircle size={16} />
                    <span>
                      {t(msCalendarErrorKey(testResult.data?.errorClass))}
                      {testResult.data?.error ? ` (${testResult.data.error})` : ''}
                    </span>
                  </ResultBox>
                )
              ) : (
                <ResultBox data-role="ms-calendar-test-error" $tone="error">
                  <XCircle size={16} />
                  <span>{t('integrations.msCalendar.testInternalError')}</span>
                </ResultBox>
              )
            )}
          </>
        )}
      </Card>

      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={t(connected ? 'integrations.msCalendar.editTitle' : 'integrations.msCalendar.connectTitle')}
        schema={drawerSchema}
        initialValues={connected
          ? { azureTenantId: status?.azureTenantId || '', clientId: status?.clientId || '', clientSecret: '' }
          : undefined}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={disconnectConfirm}
        title={t('integrations.msCalendar.disconnectConfirm.title')}
        message={t('integrations.msCalendar.disconnectConfirm.message')}
        onConfirm={handleDisconnect}
        onCancel={() => setDisconnectConfirm(false)}
        loading={disconnecting}
        type="danger"
      />
    </Wrapper>
  );
}

const Wrapper = styled.div`
  margin-top: 16px;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #E8ECF0;
  border-radius: 10px;
  padding: 22px 24px;
  max-width: 560px;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
`;

const IconWrap = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0F6CBD, #2B88D8);
  color: #fff;
  flex-shrink: 0;
`;

const CardTitle = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CardSubtitle = styled.div`
  font-size: 12px;
  color: #6c757d;
  margin-top: 2px;
`;

const Description = styled.p`
  font-size: 13px;
  color: #6c757d;
  margin: 4px 0 14px;
  line-height: 1.55;
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 0 16px;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  ${({ $tone }) => ($tone === 'ok'
    ? 'background:#DCFCE7;color:#16A34A;'
    : 'background:#F1F5F9;color:#64748B;')}
`;

const FieldList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 18px;
`;

const FieldRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 12px;
  align-items: baseline;
`;

const FieldLabel = styled.span`
  font-size: 12px;
  color: #6c757d;
  min-width: 190px;
`;

const FieldValue = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  word-break: break-all;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 4px;
`;

const ResultBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px 14px;
  border-radius: 6px;
  margin: 14px 0;
  font-size: 13px;
  line-height: 1.5;
  ${({ $tone }) => ($tone === 'success' ? `
    background: #DCFCE7;
    color: #16A34A;
  ` : $tone === 'warning' ? `
    background: #FFF7ED;
    color: #EA580C;
  ` : `
    background: #FEF2F2;
    color: #E53935;
  `)}
`;
